import './styles/carbon-overrides.scss';

import { isDefined, SuggestionRegistryProvider } from '@kaoto/forms';
import { VisualizationProvider } from '@patternfly/react-topology';
import { FunctionComponent, PropsWithChildren, useLayoutEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import { Anchors } from './components/registers/anchors';
import { NodeInteractionAddonProvider } from './components/registers/interactions/node-interaction-addon.provider';
import { getPluginMetadataApi } from './components/registers/plugin-metadata';
import { RegisterComponents } from './components/registers/RegisterComponents';
import { RegisterNodeInteractionAddons } from './components/registers/RegisterNodeInteractionAddons';
import { RenderingProvider } from './components/RenderingAnchor/rendering.provider';
import { RenderingAnchor } from './components/RenderingAnchor/RenderingAnchor';
import { ControllerService } from './components/Visualization/Canvas/controller.service';
import { CatalogLoaderProvider } from './dynamic-catalog/catalog.provider';
import { CatalogTilesProvider } from './dynamic-catalog/catalog-tiles.provider';
import { Shell } from './layout/Shell';
import { LocalStorageSettingsAdapter } from './models/settings/localstorage-settings-adapter';
import {
  EntitiesProvider,
  KaotoResourceProvider,
  KeyboardShortcutsProvider,
  MetadataProvider,
  RuntimeProvider,
  SchemasLoaderProvider,
  SettingsProvider,
  SourceCodeLocalStorageProvider,
  VisibleFlowsProvider,
} from './providers';
import { CatalogSchemaLoader } from './utils/catalog-schema-loader';
import { setColorScheme } from './utils/color-scheme';

/**
 * Mounts the metadata provider only when an installed extension supplied an API, so with no
 * extension the context stays undefined and every consumer behaves as it always has.
 */
const WithPluginMetadata: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const api = getPluginMetadataApi();

  return api ? <MetadataProvider api={api}>{children}</MetadataProvider> : children;
};

const App = () => {
  const controller = useMemo(() => ControllerService.createController(), []);
  const settingsAdapter = new LocalStorageSettingsAdapter();
  let catalogUrl = CatalogSchemaLoader.DEFAULT_CATALOG_PATH;
  const {
    catalogUrl: settingsCatalogUrl,
    runtimeCatalogName,
    testingCatalogName,
    colorScheme,
  } = settingsAdapter.getSettings();

  if (isDefined(settingsCatalogUrl) && settingsCatalogUrl !== '') {
    catalogUrl = settingsCatalogUrl;
  }

  useLayoutEffect(() => {
    setColorScheme(colorScheme);
  }, [colorScheme]);

  return (
    <SettingsProvider adapter={settingsAdapter}>
      <SourceCodeLocalStorageProvider>
        <KaotoResourceProvider>
          <RuntimeProvider
            catalogUrl={catalogUrl}
            runtimeCatalogName={runtimeCatalogName}
            testingCatalogName={testingCatalogName}
          >
            <SchemasLoaderProvider>
              <CatalogLoaderProvider>
                <EntitiesProvider>
                  <Shell>
                    <CatalogTilesProvider>
                      <VisualizationProvider controller={controller}>
                        <VisibleFlowsProvider>
                          <RenderingProvider>
                            <RegisterComponents>
                              <NodeInteractionAddonProvider>
                                <RegisterNodeInteractionAddons>
                                  <SuggestionRegistryProvider>
                                    <KeyboardShortcutsProvider>
                                      <WithPluginMetadata>
                                        <div className="shell__workspace">
                                          <RenderingAnchor anchorTag={Anchors.WorkspaceHeader} />
                                          <div className="shell__editor">
                                            <Outlet />
                                          </div>
                                        </div>
                                      </WithPluginMetadata>
                                    </KeyboardShortcutsProvider>
                                  </SuggestionRegistryProvider>
                                </RegisterNodeInteractionAddons>
                              </NodeInteractionAddonProvider>
                            </RegisterComponents>
                          </RenderingProvider>
                        </VisibleFlowsProvider>
                      </VisualizationProvider>
                    </CatalogTilesProvider>
                  </Shell>
                </EntitiesProvider>
              </CatalogLoaderProvider>
            </SchemasLoaderProvider>
          </RuntimeProvider>
        </KaotoResourceProvider>
      </SourceCodeLocalStorageProvider>
    </SettingsProvider>
  );
};

export default App;
